// hooks/useInitializeContext.ts
import { useEffect, useMemo, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  DecodedAccessToken,
  DecodedAzureAccessToken,
} from '@/hooks/private/useAccessToken';
import { useAccessToken } from './useAccessToken';
import { createApiClients } from '@/lib/createApiClients';
import { PrivateContextData } from '@/components/private/PrivateContext';
import { RoleUserOrgAssociationDto } from '@/apiClient';
import { tokenStorage } from '@/services/tokenStorage';

// no-dd-sa:typescript-code-style/max-params
export function useInitializeContext(
  azureAccessToken?: string,
  selectedOrganisationId?: number,
  userOrganisations?: PrivateContextData['userOrganisations'],
  setContextData?: (data: PrivateContextData) => void,
  setUserOrganisations?: (orgs: RoleUserOrgAssociationDto[]) => void,
  setSelectedOrganisationId?: (id: number) => void,
) {
  // Track if we've already initialized with these tokens
  const hasInitializedRef = useRef(false);
  const lastTokenRef = useRef<string | undefined>();

  // Check if we're using stored tokens
  const isUsingStoredTokens = azureAccessToken === 'stored-token-placeholder';

  // Get stored tokens if using them - memoize to prevent infinite loops
  const storedTokens = useMemo(() => {
    if (isUsingStoredTokens) {
      return tokenStorage.getTokens();
    }
    return null;
  }, [isUsingStoredTokens]);

  // Skip normal flow if using stored tokens
  const { data: accessTokenResponse } = useAccessToken(
    isUsingStoredTokens ? undefined : azureAccessToken,
  );

  useEffect(() => {
    if (!setContextData) return;

    // Prevent re-initialization if we already have the necessary data
    if (!azureAccessToken && !isUsingStoredTokens) return;
    if (isUsingStoredTokens && !storedTokens) return;
    if (!isUsingStoredTokens && !accessTokenResponse) return;

    let token: string;
    let refreshToken: string;
    let userId: string | undefined;
    let userEmail: string | undefined;
    let userGuid: string;
    let b2cFirstName: string | undefined;
    let b2cLastName: string | undefined;
    let emails: string[] | undefined;

    if (isUsingStoredTokens && storedTokens) {
      // Use stored tokens
      token = storedTokens.token;
      refreshToken = storedTokens.refreshToken;

      // Check if we've already initialized with this token
      if (hasInitializedRef.current && lastTokenRef.current === token) {
        return;
      }

      console.log('[Auth] Initializing context with stored tokens');
      hasInitializedRef.current = true;
      lastTokenRef.current = token;

      // Decode stored token for user info
      const decodedToken = jwtDecode<DecodedAccessToken>(token);
      userId = decodedToken.UserId;
      userEmail = decodedToken.UserEmail;
      userGuid = decodedToken.UserGuid;

      // We don't have Azure token info when using stored tokens
      // Set defaults or retrieve from token if available
      b2cFirstName = undefined;
      b2cLastName = undefined;
      emails = userEmail ? [userEmail] : undefined;
    } else if (
      accessTokenResponse &&
      azureAccessToken &&
      !isUsingStoredTokens
    ) {
      // Use tokens from MSAL flow
      token = accessTokenResponse.data.token;
      refreshToken = accessTokenResponse.data.refreshToken;

      // Check if we've already initialized with this token
      if (hasInitializedRef.current && lastTokenRef.current === token) {
        return;
      }

      console.log('[Auth] Initializing context with MSAL tokens');
      hasInitializedRef.current = true;
      lastTokenRef.current = token;

      // Save tokens for future use
      tokenStorage.saveTokens(token, refreshToken);
      console.log('[Auth] Tokens saved for future sessions');

      const decodedToken = jwtDecode<DecodedAccessToken>(token);
      userId = decodedToken.UserId;
      userEmail = decodedToken.UserEmail;
      userGuid = decodedToken.UserGuid;

      // Decode Azure token for additional info
      const azureDecoded = jwtDecode<DecodedAzureAccessToken>(azureAccessToken);
      emails = azureDecoded.emails;
      b2cFirstName = azureDecoded.given_name;
      b2cLastName = azureDecoded.family_name;
    } else {
      // Waiting for tokens
      return;
    }

    const {
      axiosInstance,
      elsaApi,
      userApi,
      userProfileApi,
      validationApi,
      ...apiClients
    } = createApiClients({
      refreshToken,
      token,
      onTokenRefresh: (
        newToken: string,
        newRefreshToken: string | undefined,
      ) => {
        console.log('[Auth] Tokens refreshed, saving to storage');
        if (!newRefreshToken) {
          console.warn('[Auth] No refresh token provided on refresh');
          return;
        }
        // Save updated tokens
        tokenStorage.saveTokens(newToken, newRefreshToken);

        // Recreate API clients with new tokens
        const newClients = createApiClients({
          refreshToken: newRefreshToken,
          token: newToken,
          onTokenRefresh: () => {}, // Placeholder, will be replaced by recursive call
        });

        setContextData({
          ...newClients,
          refreshToken: newRefreshToken,
          token: newToken,
          userId: userId ? parseInt(userId, 10) : undefined,
          userGuid,
          userEmail: userEmail || emails?.at(0),
          b2cFirstName,
          b2cLastName,
          loginFlowComplete: true,
          selectedOrganisationId,
          setSelectedOrganisationId,
          setUserOrganisations,
          userOrganisations,
        });
      },
    });

    setContextData({
      axiosInstance,
      refreshToken,
      token,
      userId: userId ? parseInt(userId, 10) : undefined,
      userGuid,
      userEmail: userEmail || emails?.at(0),
      b2cFirstName,
      b2cLastName,
      loginFlowComplete: true,
      elsaApi,
      userApi,
      userProfileApi,
      validationApi,
      selectedOrganisationId,
      setSelectedOrganisationId,
      setUserOrganisations,
      userOrganisations,
      ...apiClients,
    });
  }, [
    accessTokenResponse,
    azureAccessToken,
    isUsingStoredTokens,
    selectedOrganisationId,
    storedTokens,
    userOrganisations,
    setContextData,
    setUserOrganisations,
    setSelectedOrganisationId,
  ]);
}
